import React, { useState, lazy, Suspense, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "./core/components/Header";
import { Footer } from "./core/components/Footer";
import { Landing } from "./core/components/Landing";
import { HowItWorks } from "./core/components/HowItWorks";
import { Tutorials } from "./core/components/Tutorials";
import { PrivacyPolicy } from "./core/components/PrivacyPolicy";
import { ContactUs } from "./core/components/ContactUs";
import { TermsAndConditions } from "./core/components/TermsAndConditions";
import { Sitemap } from "./core/components/Sitemap";
import { WhatsNew } from "./core/components/WhatsNew";
import BlogFlow from "./flows/blog/BlogFlow";
import { useBeforeUnload } from "./core/hooks/useBeforeUnload";
import { scrollToTop } from "./core/utils/scrollToTop";
import { initializeRouter, updateUrl, getRouteFromPath, type Route } from "./core/utils/router";
import { updatePageSEO, SEO_CONFIG } from "./core/utils/seo";
import { AdExclusionManager } from "./core/components/AdExclusionManager";
import { FeedbackProvider } from "./core/context/FeedbackContext";
import { FeedbackForm, FeedbackTrigger } from "./core/components/FeedbackForm";

const EDITING_FLOWS: Route[] = [
  "image-to-video",
  "thumbnail",
  "banner",
  "video-editor",
  "auto-captions",
  "shorts-clipper",
  "video-composer-v3",
  "video-stack-composer",
];

const DARK_FLOWS: Route[] = [
  "image-to-video",
  "thumbnail",
  "banner",
  "video-editor",
  "auto-captions",
  "shorts-clipper",
  "video-composer-v3",
  "video-stack-composer",
  "gif-creator",
];

// Lazy load heavy editing tools for better initial load performance
const ImageToVideoFlow = lazy(() => import("./flows/image-to-video/ImageToVideoFlow").then(m => ({ default: m.ImageToVideoFlow })));
const ThumbnailFlow = lazy(() => import("./flows/thumbnail-creator/ThumbnailFlow").then(m => ({ default: m.ThumbnailFlow })));
const VideoEditorFlow = lazy(() => import("./flows/video-editor/VideoEditorFlow").then(m => ({ default: m.VideoEditorFlow })));
const AutoCaptionsFlow = lazy(() => import("./flows/auto-captions/AutoCaptionsFlow").then(m => ({ default: m.AutoCaptionsFlow })));
const ShortsClipperFlow = lazy(() => import("./flows/shorts-clipper/ShortsClipperFlow").then(m => ({ default: m.ShortsClipperFlow })));
const VideoComposerV3Flow = lazy(() => import("./flows/video-composer-v3/VideoComposerV3Flow").then(m => ({ default: m.VideoComposerV3Flow })));
const SimpleComposer2Flow = lazy(() => import("./flows/simple-composer-2.0/SimpleComposer2Flow"));
const GifCreatorFlow = lazy(() => import("./flows/gif-creator/GifCreatorFlow"));

// Loading fallback component
const LoadingSpinner = ({ dark }: { dark?: boolean }) => (
  <div className={`min-h-screen flex items-center justify-center ${dark ? 'bg-surface-base text-text-secondary' : 'bg-white text-gray-700'}`}>
    <div className="text-center">
      <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 ${dark ? 'border-accent-primary border-t-transparent' : 'border-blue-500 border-t-transparent'}`}></div>
      <p className="mt-4 text-lg">{dark ? 'Loading editor…' : 'Loading...'}</p>
    </div>
  </div>
);

export default function App() {
  const [activeFlow, setActiveFlow] = useState<Route>(() => getRouteFromPath(window.location.pathname));

  // Initialize router for URL-based navigation
  useEffect(() => {
    const handleRouteChange = (route: Route) => {
      // Update SEO based on route
      if (route === "whats-new") {
        updatePageSEO({
          title: "What's New - Latest Features & Updates | Snip Rush",
          description: "Stay updated with the latest features, improvements, and bug fixes in Snip Rush. See what's new in our video editing tools.",
          keywords: "snip rush updates, video editor features, changelog, new features, video editing updates",
          canonical: "https://sniprush.com/whats-new",
        });
      }
      if (route === "blog-post") {
        // For blog-post routes, we need to check if the slug exists
        const path = window.location.pathname;
        if (path.startsWith('/blog/') && path !== '/blog') {
          setActiveFlow("blog-post");
        } else {
          setActiveFlow("blog");
        }
      } else {
        setActiveFlow(route);
      }
    };

    initializeRouter(handleRouteChange);

    // Also listen for routechange events (for programmatic navigation)
    const handleRouteChangeEvent = () => {
      const route = getRouteFromPath(window.location.pathname);
      handleRouteChange(route);
    };

    window.addEventListener('routechange', handleRouteChangeEvent);

    return () => {
      window.removeEventListener('routechange', handleRouteChangeEvent);
    };
  }, []);

  // Scroll to top and update SEO whenever the active flow changes
  useEffect(() => {
    scrollToTop();
    
    // Update page SEO metadata
    const seoData = SEO_CONFIG[activeFlow];
    if (seoData) {
      updatePageSEO(seoData);
    }
  }, [activeFlow]);

  // Warn user before leaving if they're in an editing flow
  useBeforeUnload(
    EDITING_FLOWS.includes(activeFlow),
    "You have unsaved work. Are you sure you want to leave?"
  );

  const handleFlowChange = React.useCallback((newFlow: Route) => {
    if (EDITING_FLOWS.includes(activeFlow)) {
      const confirmLeave = window.confirm("You have unsaved work. Leave without saving?");
      if (!confirmLeave) return;
    }
    // For blog routes, force a clean hard reload (no query params) with a short guard
    if (newFlow === 'blog' || newFlow === 'blog-post') {
      const KEY = 'blog-hard-nav-once';
      const now = Date.now();
      const last = Number(sessionStorage.getItem(KEY) || '0');
      const WINDOW_MS = 1500;
      if (!last || now - last > WINDOW_MS) {
        sessionStorage.setItem(KEY, String(now));
        window.location.assign('/blog');
        return;
      }
    }
    setActiveFlow(newFlow);
    updateUrl(newFlow);
  }, [activeFlow]);

  const handleBackToLanding = React.useCallback(() => {
    handleFlowChange("landing");
  }, [handleFlowChange]);

  const isDarkFlow = DARK_FLOWS.includes(activeFlow);

  return (
    <FeedbackProvider>
      <div className={`min-h-screen transition-colors ${isDarkFlow ? 'bg-surface-base text-text-primary flow-theme-dark' : 'bg-white text-gray-900'}`}>
        <AdExclusionManager currentRoute={activeFlow} />
        {activeFlow !== "video-composer-v3" && activeFlow !== "video-stack-composer" && <Header onLogoClick={handleBackToLanding} onHowItWorksClick={() => handleFlowChange("how-it-works")} onTutorialsClick={() => handleFlowChange("tutorials")} onBlogClick={() => handleFlowChange("blog")} onWhatsNewClick={() => handleFlowChange("whats-new")} />}
      <AnimatePresence mode="wait">
        {activeFlow === "landing" && (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Landing
              onStartVideo={() => handleFlowChange("image-to-video")}
              onStartThumbnail={() => handleFlowChange("thumbnail")}
              onStartBanner={() => handleFlowChange("banner")}
              onStartVideoEditor={() => handleFlowChange("video-editor")}
              onStartAutoCaptions={() => handleFlowChange("auto-captions")}
              onStartShortsClipper={() => handleFlowChange("shorts-clipper")}
              onStartAllInOne={() => handleFlowChange("video-composer-v3")}
              onStartSimpleComposer2={() => handleFlowChange("video-stack-composer")}
              onStartGifCreator={process.env.NODE_ENV === 'development' ? () => handleFlowChange("gif-creator") : () => {}}
              onTutorialsClick={() => handleFlowChange("tutorials")}
            />
          </motion.div>
        )}
        {activeFlow === "image-to-video" && (
          <motion.div
            key="image-to-video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <ImageToVideoFlow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "thumbnail" && (
          <motion.div
            key="thumbnail"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <ThumbnailFlow key="thumbnail-portrait" onBack={() => handleFlowChange("landing")} initialOrientation="portrait" />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "banner" && (
          <motion.div
            key="banner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <ThumbnailFlow key="thumbnail-banner" onBack={() => handleFlowChange("landing")} initialOrientation="banner" />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "video-editor" && (
          <motion.div
            key="video-editor"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <VideoEditorFlow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "auto-captions" && (
          <motion.div
            key="auto-captions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <AutoCaptionsFlow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "shorts-clipper" && (
          <motion.div
            key="shorts-clipper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <ShortsClipperFlow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "video-composer-v3" && (
          <motion.div
            key="video-composer-v3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <VideoComposerV3Flow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "video-stack-composer" && (
          <motion.div
            key="video-stack-composer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <SimpleComposer2Flow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "how-it-works" && (
          <motion.div
            key="how-it-works"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <HowItWorks 
              onBack={() => handleFlowChange("landing")} 
              onStartCreating={() => handleFlowChange("landing")}
            />
          </motion.div>
        )}
        {activeFlow === "tutorials" && (
          <motion.div
            key="tutorials"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Tutorials 
            onBack={() => handleFlowChange("landing")}
            onViewAllTools={() => handleFlowChange("landing")}
            onStartAutoCaptions={() => handleFlowChange("auto-captions")}
            onStartVideoEditor={() => handleFlowChange("video-editor")}
            onStartThumbnail={() => handleFlowChange("thumbnail")}
            onStartBanner={() => handleFlowChange("banner")}
            onStartVideo={() => handleFlowChange("image-to-video")}
            onStartShortsClipper={() => handleFlowChange("shorts-clipper")}
            />
          </motion.div>
        )}
        {activeFlow === "privacy-policy" && (
          <motion.div
            key="privacy-policy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <PrivacyPolicy onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "contact-us" && (
          <motion.div
            key="contact-us"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ContactUs onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "terms-and-conditions" && (
          <motion.div
            key="terms-and-conditions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TermsAndConditions onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "gif-creator" && process.env.NODE_ENV === 'development' && (
          <motion.div
            key="gif-creator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner dark={isDarkFlow} />}>
              <GifCreatorFlow onBack={() => handleFlowChange("landing")} />
            </Suspense>
          </motion.div>
        )}
        {activeFlow === "sitemap" && (
          <motion.div
            key="sitemap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Sitemap onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {(activeFlow === "blog" || activeFlow === "blog-post") && (
          <motion.div
            key="blog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <BlogFlow onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "whats-new" && (
          <motion.div
            key="whats-new"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <WhatsNew />
          </motion.div>
        )}
        {activeFlow === "privacy-policy" && (
          <motion.div
            key="privacy-policy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <PrivacyPolicy onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "contact-us" && (
          <motion.div
            key="contact-us"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ContactUs onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "terms-and-conditions" && (
          <motion.div
            key="terms-and-conditions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TermsAndConditions onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
        {activeFlow === "sitemap" && (
          <motion.div
            key="sitemap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Sitemap onBack={() => handleFlowChange("landing")} />
          </motion.div>
        )}
      </AnimatePresence>
      {activeFlow !== "video-composer-v3" && activeFlow !== "video-stack-composer" && <Footer onPrivacyPolicyClick={() => handleFlowChange("privacy-policy")} onContactUsClick={() => handleFlowChange("contact-us")} onTermsClick={() => handleFlowChange("terms-and-conditions")} />}
        
        {/* Global Feedback Components - Only show trigger in development */}
        {process.env.REACT_APP_SHOW_FEEDBACK === 'true' && <FeedbackTrigger />}
        <FeedbackForm />
      </div>
    </FeedbackProvider>
  );
}
